import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, CheckCircle2, Pencil, Trash2, Reply, X, Send, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#E2DACB] px-3 py-2.5 text-sm text-[#233D35] focus:border-[#C58A47] focus:outline-none transition-colors";

const btnBase = "inline-flex items-center gap-1.5 font-head font-bold uppercase tracking-[0.1em] text-xs px-3.5 py-2 transition-colors";

const ReviewCard = ({ r, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [responding, setResponding] = useState(false);
  const [draft, setDraft] = useState({ name: r.name, business: r.business, message: r.message });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const responded = !!r.responded;

  const approve = async () => {
    await axios.post(`${API}/admin/reviews/${r.id}/approve`, {}, { withCredentials: true });
    toast.success("Review approved — it's live on the homepage.");
    await onChange();
  };

  const remove = async () => {
    if (!window.confirm(`Delete the review from ${r.name}? This can't be undone.`)) return;
    await axios.delete(`${API}/admin/reviews/${r.id}`, { withCredentials: true });
    toast.success("Review deleted.");
    await onChange();
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/reviews/${r.id}`, draft, { withCredentials: true });
      toast.success("Review updated.");
      setEditing(false);
      await onChange();
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleResponded = async () => {
    await axios.put(`${API}/admin/reviews/${r.id}`, { responded: !responded }, { withCredentials: true });
    await onChange();
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/reviews/${r.id}/respond`, { message: reply }, { withCredentials: true });
      toast.success(`Reply sent to ${r.email}.`);
      setResponding(false);
      setReply("");
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the reply. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2DACB] p-5 sm:p-6" data-testid={`review-item-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-head font-bold uppercase tracking-tight text-xl text-[#233D35]">{r.name}</span>
            <span className={`font-head font-semibold uppercase tracking-[0.14em] text-[10px] px-2 py-1 ${
              r.status === "approved" ? "bg-[#233D35] text-[#F3EFE5]" : "bg-[#C58A47] text-[#F3EFE5]"
            }`} data-testid={`review-status-${r.id}`}>
              {r.status === "approved" ? "Live" : "Pending"}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 font-head font-semibold uppercase tracking-[0.14em] text-[10px] px-2 py-1 ${
                responded ? "bg-[#233D35] text-[#F3EFE5]" : "bg-white border border-[#C58A47] text-[#C58A47]"
              }`}
              data-testid={`review-responded-${r.id}`}
            >
              {responded ? <><Check size={12} /> Responded</> : "Not responded yet"}
            </span>
          </div>
          {r.business && <span className="text-sm text-[#314F64]">{r.business}</span>}
          {r.email && (
            <div className="text-sm mt-0.5">
              <a href={`mailto:${r.email}`} className="text-[#C58A47] hover:underline">{r.email}</a>
            </div>
          )}
          <div className="flex gap-0.5 mt-1.5" aria-label="5 out of 5 stars">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={13} className="text-[#C58A47]" fill="#C58A47" strokeWidth={1} />
            ))}
          </div>
        </div>
      </div>

      <p className="mt-3 text-sm text-[#233D35]/90 leading-relaxed border-t border-[#E2DACB] pt-3">&ldquo;{r.message}&rdquo;</p>
      <span className="block text-xs text-[#77786F]/70 mt-2">{new Date(r.created_at).toLocaleString()}</span>

      <div className="mt-4 flex flex-wrap gap-2">
        {r.status !== "approved" && (
          <button onClick={approve} data-testid={`review-approve-${r.id}`}
            className={`${btnBase} bg-[#233D35] text-[#F3EFE5] hover:bg-[#1A2621]`}>
            <Check size={14} /> Approve
          </button>
        )}
        {r.email ? (
          <button onClick={() => { setResponding(!responding); setEditing(false); }} data-testid={`review-respond-${r.id}`}
            className={`${btnBase} bg-[#C58A47] text-[#F3EFE5] hover:bg-[#B0793A]`}>
            <Reply size={14} /> Respond
          </button>
        ) : (
          <span className="text-xs text-[#77786F]/70 self-center" data-testid={`review-no-email-${r.id}`}>No email on file — can't respond</span>
        )}
        <button onClick={() => { setEditing(!editing); setResponding(false); setDraft({ name: r.name, business: r.business, message: r.message }); }} data-testid={`review-edit-${r.id}`}
          className={`${btnBase} border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5]`}>
          <Pencil size={14} /> Edit
        </button>
        <button onClick={toggleResponded} data-testid={`review-toggle-${r.id}`}
          className={`${btnBase} border ${responded ? "border-[#C58A47] text-[#C58A47]" : "border-[#233D35] text-[#233D35]"} hover:opacity-80`}>
          <CheckCircle2 size={14} /> {responded ? "Mark Not Responded" : "Mark Responded"}
        </button>
        <button onClick={remove} data-testid={`review-delete-${r.id}`}
          className={`${btnBase} border border-red-300 text-red-700 hover:bg-red-50`}>
          <Trash2 size={14} /> Delete
        </button>
      </div>

      {responding && r.email && (
        <div className="mt-4 border-t border-[#E2DACB] pt-4" data-testid={`review-respond-form-${r.id}`}>
          <label className="block text-sm font-semibold text-[#233D35] mb-1.5">Reply to {r.email}</label>
          <textarea rows={4} className={inputCls} value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder="Thank them for the kind words — it arrives as an email from Good Work Web Co."
            data-testid={`review-reply-text-${r.id}`} />
          <div className="mt-3 flex gap-2">
            <button onClick={sendReply} disabled={busy || !reply.trim()} data-testid={`review-reply-send-${r.id}`}
              className={`${btnBase} bg-[#233D35] text-[#F3EFE5] hover:bg-[#1A2621] disabled:opacity-60`}>
              <Send size={14} /> {busy ? "Sending…" : "Send Reply"}
            </button>
            <button onClick={() => setResponding(false)} className={`${btnBase} border border-[#E2DACB] text-[#77786F]`} data-testid={`review-reply-cancel-${r.id}`}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-4 border-t border-[#E2DACB] pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`review-edit-form-${r.id}`}>
          <div>
            <label className="block text-xs font-semibold text-[#233D35] mb-1">Name</label>
            <input className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid={`review-edit-name-${r.id}`} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#233D35] mb-1">Business</label>
            <input className={inputCls} value={draft.business || ""} onChange={(e) => setDraft({ ...draft, business: e.target.value })} data-testid={`review-edit-business-${r.id}`} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#233D35] mb-1">Review text</label>
            <textarea rows={3} className={inputCls} value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} data-testid={`review-edit-message-${r.id}`} />
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={save} disabled={busy} data-testid={`review-edit-save-${r.id}`}
              className={`${btnBase} bg-[#233D35] text-[#F3EFE5] hover:bg-[#1A2621] disabled:opacity-60`}>
              <Check size={14} /> {busy ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={() => setEditing(false)} className={`${btnBase} border border-[#E2DACB] text-[#77786F]`} data-testid={`review-edit-cancel-${r.id}`}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
